import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import type {
  ColumnOrderState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, Search, Settings2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { API_BASE } from "@/config/api";
import { fetchWithAuth } from "@/lib/api";
import { selectPreferredAssetId } from "@/lib/preferred-asset";
import type { Asset } from "@/schemas";
import type { ScreenerFilter } from "@/schemas/screener.schema";

import { ScreenerColumnsSettingsModal } from "./ScreenerColumnsSettingsModal";
import { getScreenerColumns, type ScreenerAsset } from "./columns";
import { ScreenerDataTable } from "./data-table";
import { ScreenerFilters } from "./ScreenerFilters";
import { useScreenerPreferences } from "../hooks/use-screener-preferences";
import { ScreenerService } from "../services/screener-service";
import {
  type ScreenerScope,
  getScreenerDefaultOrder,
  getScreenerDefaultSort,
  getScreenerDefaultVisibility,
  normalizeScreenerColumnId,
  normalizeScreenerOrder,
  normalizeScreenerVisibility,
  sanitizePersistedScreenerFilters,
} from "../utils/screener-preferences";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

const SORT_FIELD_MAP: Record<string, string> = {
  company: "name",
  sector: "sector",
  category: "category",
  market_cap: "market_cap_usd",
  market_cap_usd: "market_cap_usd",
  net_assets: "net_assets",
  latest_price: "latest_price",
  daily_change_percent: "daily_change_percent",
  trailing_pe: "trailing_pe",
  forward_pe: "forward_pe",
  peg_ratio: "peg_ratio",
  price_to_book: "price_to_book",
  latest_price_to_sales: "latest_price_to_sales",
  latest_price_to_earnings: "latest_price_to_earnings",
  latest_price_to_fcf: "latest_price_to_fcf",
  dividend_yield: "dividend_yield",
  yield: "yield",
  return_on_equity: "return_on_equity",
  return_on_assets: "return_on_assets",
  revenue_growth: "revenue_growth",
  earnings_quarterly_growth: "earnings_growth",
  gross_margins: "gross_margins",
  operating_margins: "operating_margins",
  profit_margins: "profit_margins",
  current_ratio: "current_ratio",
  quick_ratio: "quick_ratio",
  payout_ratio: "payout_ratio",
  beta: "beta",
  short_ratio: "short_ratio",
  average_volume: "average_volume",
  float_shares: "float_shares",
  held_percent_insiders: "held_percent_insiders",
  held_percent_institutions: "held_percent_institutions",
  fifty_two_week_high: "fifty_two_week_high",
  fifty_two_week_low: "fifty_two_week_low",
  fifty_day_average: "fifty_day_average",
  two_hundred_day_average: "two_hundred_day_average",
  expense_ratio: "expense_ratio",
  ytd_return: "ytd_return",
  three_year_avg_return: "three_year_avg_return",
  five_year_avg_return: "five_year_avg_return",
  beta_3year: "beta_3year",
  fund_family: "fund_family",
};

type AssetTab = "stocks" | "etf" | "crypto";

const TABS: { value: AssetTab; label: string; types: string[] }[] = [
  { value: "stocks", label: "Stocks", types: ["stock"] },
  { value: "etf", label: "ETFs", types: ["etf"] },
  { value: "crypto", label: "Crypto", types: ["crypto"] },
];

function getGroupByForTab(tab: AssetTab): ScreenerFilter["group_by"] {
  if (tab === "stocks") return "name";
  if (tab === "etf") return "isin";
  return undefined;
}

function createBaseFilter(tab: AssetTab): ScreenerFilter {
  return {
    types: TABS.find((item) => item.value === tab)?.types,
    limit: PAGE_SIZE,
    offset: 0,
    sort_by: getScreenerDefaultSort(tab === "etf" ? "etf" : "stocks").sortBy,
    sort_order: getScreenerDefaultSort(tab === "etf" ? "etf" : "stocks")
      .sortOrder,
    group_by: getGroupByForTab(tab),
  };
}

export default function ScreenerPage() {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<AssetTab>("stocks");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ScreenerFilter>(
    createBaseFilter("stocks"),
  );
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "market_cap_usd", desc: true },
  ]);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    getScreenerDefaultOrder("stocks"),
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    getScreenerDefaultVisibility("stocks"),
  );
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);

  const isPersistedScope = activeTab === "stocks" || activeTab === "etf";
  const persistedScope = (
    activeTab === "etf" ? "etf" : "stocks"
  ) as ScreenerScope;
  const prefsQuery = useScreenerPreferences(persistedScope);
  const hydratedScopes = useRef<Record<string, boolean>>({});

  const columns = useMemo(
    () => getScreenerColumns(activeTab === "etf" ? "etf" : "stocks"),
    [activeTab],
  );

  useEffect(() => {
    const base = createBaseFilter(activeTab);
    if (!isPersistedScope) {
      hydratedScopes.current.crypto = true;
      setFilter(base);
      setSorting([
        { id: normalizeScreenerColumnId(base.sort_by ?? "market_cap_usd"), desc: true },
      ]);
      setColumnOrder(getScreenerDefaultOrder("stocks"));
      setColumnVisibility(getScreenerDefaultVisibility("stocks"));
      return;
    }

    if (prefsQuery.isLoading || hydratedScopes.current[persistedScope]) {
      return;
    }

    const saved = prefsQuery.data;
    const defaultSort = getScreenerDefaultSort(persistedScope);
    setFilter({
      ...base,
      ...(saved?.active_filters as Partial<ScreenerFilter> | undefined),
      search: "",
      sort_by: normalizeScreenerColumnId(saved?.sort_by ?? defaultSort.sortBy),
      sort_order: saved?.sort_order ?? defaultSort.sortOrder,
    });
    setSearchInput(saved?.advanced_search ?? "");
    setSorting([
      {
        id: normalizeScreenerColumnId(saved?.sort_by ?? defaultSort.sortBy),
        desc: (saved?.sort_order ?? defaultSort.sortOrder) === "desc",
      },
    ]);
    setColumnOrder(
      normalizeScreenerOrder(
        persistedScope,
        saved?.column_order ?? getScreenerDefaultOrder(persistedScope),
      ),
    );
    setColumnVisibility(
      normalizeScreenerVisibility(
        persistedScope,
        saved?.visibility ?? getScreenerDefaultVisibility(persistedScope),
      ),
    );
    hydratedScopes.current[persistedScope] = true;
  }, [
    activeTab,
    isPersistedScope,
    persistedScope,
    prefsQuery.data,
    prefsQuery.isLoading,
  ]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    setFilter((prev) => ({ ...prev, offset: 0 }));
  }, [debouncedSearch]);

  useEffect(() => {
    if (!isPersistedScope || !hydratedScopes.current[persistedScope]) return;
    const timeoutId = window.setTimeout(() => {
      void ScreenerService.putScreenerPreference(persistedScope, {
        column_order: normalizeScreenerOrder(persistedScope, columnOrder),
        visibility: normalizeScreenerVisibility(
          persistedScope,
          columnVisibility,
        ),
        sort_by:
          filter.sort_by ?? getScreenerDefaultSort(persistedScope).sortBy,
        sort_order:
          (filter.sort_order as "asc" | "desc" | undefined) ??
          getScreenerDefaultSort(persistedScope).sortOrder,
        active_filters: sanitizePersistedScreenerFilters(filter),
        advanced_search: searchInput,
      });
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [columnOrder, columnVisibility, filter, isPersistedScope, persistedScope, searchInput]);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const shouldUseLightSearch = debouncedSearch.length >= MIN_SEARCH_LENGTH;

      if (shouldUseLightSearch) {
        const searchUrl = new URL(`${API_BASE}/assets/search`);
        searchUrl.searchParams.append("q", debouncedSearch);
        searchUrl.searchParams.append(
          "asset_types",
          TABS.find((tab) => tab.value === activeTab)?.types.join(",") ?? "",
        );

        const response = await fetchWithAuth(searchUrl.toString());
        if (!response.ok) {
          throw new Error("Failed to search assets");
        }

        const data = await response.json();
        const searchAssets = (data.results || []) as Asset[];
        setAssets(searchAssets);
        setTotalCount(searchAssets.length);
        return;
      }

      const result = await ScreenerService.filterAssets({
        ...filter,
        search: undefined,
      });
      const nextAssets = result.assets || [];
      const nextTotalCount = result.total_count || 0;

      if (nextAssets.length === 0 && nextTotalCount > 0 && (filter.offset || 0) > 0) {
        const lastValidOffset = Math.max(
          Math.floor((nextTotalCount - 1) / PAGE_SIZE) * PAGE_SIZE,
          0,
        );

        if (lastValidOffset !== (filter.offset || 0)) {
          setFilter((prev) => ({ ...prev, offset: lastValidOffset }));
          return;
        }
      }

      setAssets(nextAssets);
      setTotalCount(nextTotalCount);
    } catch (error) {
      console.error("Error loading assets:", error);
      setAssets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [activeTab, debouncedSearch, filter]);

  useEffect(() => {
    void loadAssets();
  }, [loadAssets]);

  const handleSortingChange = (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const nextSorting =
      typeof updater === "function" ? updater(sorting) : updater;
    setSorting(nextSorting);
    if (nextSorting.length === 0) return;
    const [first] = nextSorting;
    setFilter((prev) => ({
      ...prev,
      sort_by: SORT_FIELD_MAP[first.id] ?? first.id,
      sort_order: first.desc ? "desc" : "asc",
      offset: 0,
    }));
  };

  const handleTabChange = (tab: AssetTab) => {
    setActiveTab(tab);
    const nextScope = tab === "etf" ? "etf" : "stocks";
    const defaultSort = getScreenerDefaultSort(nextScope);
    setFilter(createBaseFilter(tab));
    setSearchInput("");
    setDebouncedSearch("");
    setSorting([
      { id: defaultSort.sortBy, desc: defaultSort.sortOrder === "desc" },
    ]);
    setColumnOrder(getScreenerDefaultOrder(nextScope));
    setColumnVisibility(getScreenerDefaultVisibility(nextScope));
    setAssets([]);
    setTotalCount(0);
  };

  const handleFilterChange = (nextFilter: ScreenerFilter) => {
    setFilter({
      ...nextFilter,
      search: "",
      types: TABS.find((tab) => tab.value === activeTab)?.types,
      limit: PAGE_SIZE,
      group_by: getGroupByForTab(activeTab),
    });
  };

  const handleClearFilters = () => {
    handleFilterChange({
      ...createBaseFilter(activeTab),
      search: "",
    });
  };

  const handlePageChange = (nextPage: number) => {
    const safePage = Math.max(1, nextPage);

    setFilter((prev) => ({
      ...prev,
      types: TABS.find((tab) => tab.value === activeTab)?.types,
      limit: PAGE_SIZE,
      group_by: getGroupByForTab(activeTab),
      offset: (safePage - 1) * PAGE_SIZE,
    }));
  };

  const currentPage = Math.floor((filter.offset || 0) / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleRowClick = (asset: ScreenerAsset) => {
    let targetAssetId = asset.asset_id;

    if (asset.variants && asset.variants.length > 0) {
      targetAssetId =
        selectPreferredAssetId(asset.variants, asset.asset_id) ?? asset.asset_id;
    }

    router.navigate({
      to: "/screener/$assetId",
      params: { assetId: targetAssetId },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Screener</h1>
          <p className="text-muted-foreground">
            Explore assets with filters and customizable columns for stocks and
            ETFs.
          </p>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
        <Input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search assets..."
          className="h-11 bg-card pl-10 text-base shadow-sm"
        />
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="space-y-3 p-6">
            {Array.from({ length: 10 }).map((_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <ScreenerDataTable
            columns={columns}
            data={assets as ScreenerAsset[]}
            sorting={sorting}
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            onSortingChange={handleSortingChange}
            onColumnOrderChange={setColumnOrder}
            onColumnVisibilityChange={setColumnVisibility}
            onRowClick={handleRowClick}
            toolbarLeft={
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1 rounded-lg bg-muted p-1">
                    {TABS.map((tab) => (
                      <Button
                        key={tab.value}
                        type="button"
                        variant={activeTab === tab.value ? "default" : "ghost"}
                        size="sm"
                        onClick={() => handleTabChange(tab.value)}
                      >
                        {tab.label}
                      </Button>
                    ))}
                  </div>

                  <ScreenerFilters
                    filter={filter}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    assetType={activeTab}
                  />
                </div>

                <div className="text-sm text-muted-foreground">
                  {debouncedSearch.length >= MIN_SEARCH_LENGTH
                    ? `${totalCount.toLocaleString()} search results`
                    : `${totalCount.toLocaleString()} results`}
                </div>
              </div>
            }
            toolbarRight={
              activeTab === "crypto" ? null : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setColumnsModalOpen(true)}
                >
                  <Settings2 className="mr-2 h-4 w-4" />
                  Columns
                </Button>
              )
            }
          />
        )}
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {activeTab !== "crypto" && (
        <ScreenerColumnsSettingsModal
          open={columnsModalOpen}
          scope={persistedScope}
          columnOrder={columnOrder}
          visibility={columnVisibility}
          onOpenChange={setColumnsModalOpen}
          onApply={({ columnOrder: nextOrder, visibility: nextVisibility }) => {
            setColumnOrder(nextOrder);
            setColumnVisibility(nextVisibility);
          }}
        />
      )}
    </div>
  );
}
